import { useState } from 'react';
import { errorMessage } from '../api/client';
import { eventsApi, newEventId, partsApi } from '../api/endpoints';
import type { MountedPart, PartItem, PartReference, TimelineEvent } from '../api/types';
import {
  PART_OPERATION_LABELS,
  isAvailableItemState,
  partCategoryLabel,
  type PartOperation,
} from '../domain/parts';
import { useAsync } from '../hooks/useAsync';
import { fromDateTimeInput, nowForInput } from '../lib/format';
import { Field } from './Field';
import { Modal } from './Modal';
import { Notice } from './Notice';

/**
 * Un descripteur désigne soit un exemplaire, soit une référence et une
 * quantité — jamais les deux. Le mode de suivi de la référence décide, et le
 * serveur le revérifie.
 */
type Descriptor =
  | { kind: 'exemplaire'; itemId: string }
  | { kind: 'quantite'; referenceId: string; quantite: number };

function descriptorPayload(descriptor: Descriptor): Record<string, unknown> {
  return descriptor.kind === 'exemplaire'
    ? { exemplaireId: descriptor.itemId }
    : { referenceId: descriptor.referenceId, quantite: descriptor.quantite };
}

/** Choix d'une pièce à monter : une référence, puis un exemplaire ou une quantité. */
function MountedChoice({
  references,
  value,
  onChange,
}: {
  references: PartReference[];
  value: Descriptor | null;
  onChange: (descriptor: Descriptor | null) => void;
}) {
  const [referenceId, setReferenceId] = useState('');
  const reference = references.find((r) => r.id === referenceId) ?? null;

  // les exemplaires ne sont chargés qu'une fois la référence choisie, plutôt
  // qu'embarqués dans chaque ligne de la liste
  const detail = useAsync(
    (signal) =>
      referenceId === '' || reference?.tracking !== 'exemplaire'
        ? Promise.resolve({ items: [] as PartItem[] })
        : partsApi.getReference(referenceId, signal),
    [referenceId, reference?.tracking],
  );

  // seuls les exemplaires au stock ET disponibles sont montables : un
  // exemplaire retiré ou défectueux serait de toute façon refusé par le serveur
  const auStock = (detail.data?.items ?? []).filter((item) => item.workstationId === null);
  const disponibles = auStock
    .filter((item) => isAvailableItemState(item.state))
    .map((item) => ({
      id: item.id,
      label: item.serialNumber ?? `Exemplaire ${item.id.slice(0, 8)}`,
    }));
  const indisponibles = auStock.length - disponibles.length;

  return (
    <>
      <Field label="Référence">
        <select
          value={referenceId}
          required
          onChange={(event) => {
            setReferenceId(event.target.value);
            onChange(null);
          }}
        >
          <option value="">Choisir une référence…</option>
          {references.map((option) => (
            <option key={option.id} value={option.id}>
              {partCategoryLabel(option.category)} · {option.designation} — stock{' '}
              {option.stock}
            </option>
          ))}
        </select>
      </Field>

      {reference !== null && reference.tracking === 'exemplaire' && (
        <Field
          label="Exemplaire"
          hint={
            disponibles.length === 0
              ? indisponibles > 0
                ? `Aucun exemplaire disponible : ${indisponibles} au stock mais retiré(s) ou défectueux.`
                : 'Aucun exemplaire au stock pour cette référence.'
              : indisponibles > 0
                ? `${indisponibles} autre(s) au stock, non disponible(s).`
                : undefined
          }
        >
          <select
            value={value?.kind === 'exemplaire' ? value.itemId : ''}
            required
            onChange={(event) =>
              onChange(
                event.target.value === ''
                  ? null
                  : { kind: 'exemplaire', itemId: event.target.value },
              )
            }
          >
            <option value="">Choisir un exemplaire…</option>
            {disponibles.map((item) => (
              <option key={item.id} value={item.id}>
                {item.label}
              </option>
            ))}
          </select>
        </Field>
      )}

      {reference !== null && reference.tracking === 'quantite' && (
        <Field label="Quantité" hint={`Stock actuel : ${reference.stock}.`}>
          <input
            type="number"
            min={1}
            value={value?.kind === 'quantite' ? value.quantite : 1}
            required
            onChange={(event) =>
              onChange({
                kind: 'quantite',
                referenceId,
                quantite: Number.parseInt(event.target.value, 10) || 1,
              })
            }
          />
        </Field>
      )}
    </>
  );
}

/** Choix d'une pièce à retirer, parmi celles effectivement montées. */
function RemovedChoice({
  mounted,
  value,
  onChange,
}: {
  mounted: MountedPart[];
  value: Descriptor | null;
  onChange: (descriptor: Descriptor | null) => void;
}) {
  const cle = (part: MountedPart): string => part.itemId ?? `q:${part.referenceId}`;
  const choisie = mounted.find((part) => {
    if (value === null) return false;
    return value.kind === 'exemplaire'
      ? part.itemId === value.itemId
      : part.itemId === null && part.referenceId === value.referenceId;
  });

  return (
    <>
      <Field label="Pièce à retirer">
        <select
          value={choisie === undefined ? '' : cle(choisie)}
          required
          onChange={(event) => {
            const part = mounted.find((candidate) => cle(candidate) === event.target.value);
            if (part === undefined) {
              onChange(null);
              return;
            }
            onChange(
              part.itemId === null
                ? { kind: 'quantite', referenceId: part.referenceId, quantite: 1 }
                : { kind: 'exemplaire', itemId: part.itemId },
            );
          }}
        >
          <option value="">Choisir une pièce montée…</option>
          {mounted.map((part) => (
            <option key={cle(part)} value={cle(part)}>
              {partCategoryLabel(part.category)} · {part.designation}
              {part.serialNumber === null ? '' : ` — ${part.serialNumber}`}
              {part.itemId === null ? ` — ${part.quantity} montée(s)` : ''}
            </option>
          ))}
        </select>
      </Field>

      {value?.kind === 'quantite' && (
        <Field label="Quantité à retirer">
          <input
            type="number"
            min={1}
            value={value.quantite}
            required
            onChange={(event) =>
              onChange({
                ...value,
                quantite: Number.parseInt(event.target.value, 10) || 1,
              })
            }
          />
        </Field>
      )}
    </>
  );
}

export function PartChangeModal({
  operation,
  workstationId,
  references,
  mounted,
  onClose,
  onSaved,
}: {
  operation: PartOperation;
  workstationId: string;
  references: PartReference[];
  mounted: MountedPart[];
  onClose: () => void;
  onSaved: (event: TimelineEvent) => void;
}) {
  const [occurredAt, setOccurredAt] = useState(nowForInput);
  const [reason, setReason] = useState('');
  const [montee, setMontee] = useState<Descriptor | null>(null);
  const [retiree, setRetiree] = useState<Descriptor | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const attendMontee = operation === 'montage' || operation === 'remplacement';
  const attendRetiree = operation === 'retrait' || operation === 'remplacement';

  const onSubmit = (event: React.FormEvent): void => {
    event.preventDefault();
    if (attendMontee && montee === null) {
      setError('Choisissez la pièce à monter.');
      return;
    }
    if (attendRetiree && retiree === null) {
      setError('Choisissez la pièce à retirer.');
      return;
    }

    const payload: Record<string, unknown> = { operation };
    if (montee !== null && attendMontee) payload.montee = descriptorPayload(montee);
    if (retiree !== null && attendRetiree) payload.retiree = descriptorPayload(retiree);

    setBusy(true);
    setError(null);
    eventsApi
      .append({
        id: newEventId(),
        workstationId,
        type: 'changementPiece',
        occurredAt: fromDateTimeInput(occurredAt),
        reason,
        payload,
      })
      .then(({ event: saved }) => onSaved(saved))
      .catch((cause: unknown) => setError(errorMessage(cause)))
      .finally(() => setBusy(false));
  };

  return (
    <Modal
      title={PART_OPERATION_LABELS[operation]}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="bouton" onClick={onClose}>
            Annuler
          </button>
          <button
            type="submit"
            form="formulaire-piece"
            className="bouton bouton--primaire"
            disabled={busy}
          >
            {busy ? 'Enregistrement…' : 'Enregistrer au journal'}
          </button>
        </>
      }
    >
      <form id="formulaire-piece" className="pile pile--serree" onSubmit={onSubmit}>
        {error !== null && <Notice tone="erreur">{error}</Notice>}

        {operation === 'remplacement' && (
          <Notice tone="info">
            Un remplacement est le retrait de l'une et le montage de l'autre dans le même
            événement : l'annuler défait les deux.
          </Notice>
        )}

        <div className="grille-champs">
          <Field
            label="Date de l'opération"
            hint="Date à laquelle elle a eu lieu, pas celle de la saisie."
          >
            <input
              type="datetime-local"
              value={occurredAt}
              required
              onChange={(event) => setOccurredAt(event.target.value)}
            />
          </Field>

          {attendRetiree && (
            <RemovedChoice mounted={mounted} value={retiree} onChange={setRetiree} />
          )}

          {attendMontee && (
            <MountedChoice references={references} value={montee} onChange={setMontee} />
          )}

          <Field label="Motif" wide hint="Obligatoire pour ce type : visible sur la frise.">
            <textarea
              value={reason}
              required
              maxLength={2000}
              onChange={(event) => setReason(event.target.value)}
            />
          </Field>
        </div>
      </form>
    </Modal>
  );
}
